/**
 * Synapse Audio Upload Hook
 *
 * Integrates Synapse upload with the Voice Notes recording flow.
 * Uses atom-based state management via useSynapseUpload.
 *
 * This hook:
 * - Configures Synapse upload with auth and organization context
 * - Coordinates session lifecycle with recording start/stop
 * - Handles nucleus complete-upload call after preprocessing
 * - Manages UI state and analytics callbacks
 */
import { useCallback, useRef, useState } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import {
  type AmbientClientDependencies,
  type AudioDeviceInfo,
  EMPTY_TRANSCRIPT_ERROR,
  VideaNotesOPFS,
  completeAndPreprocess,
  createSession,
  directUploadChunkToWeaver,
  eventTrackerAtom,
  liveTranscriptAtom,
  noteGenerationStepAtom,
  opfs,
  recordingSessionIdAtom,
  useSynapseUpload,
} from '@repo/videa-notes';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import configurations from '@/config';
import { endpoints } from '@/react-query/endpoints';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { oktaAxiosInstance } from '@/utils/api';
import { formatPatientFirstNameAndLastName } from '@/utils/patient';
import { createDebugLogger } from '@videa/utils';

import { isEmptyTranscriptError } from '../helpers';
import { type NoteGenerationErrorProps, VideaNotesUIState } from '../types';

import type { UploadErrorMeta } from './useAudioUpload';

const debug = createDebugLogger('SynapseAudioUpload');

const { AMBIENT_API_URL } = configurations;

/**
 * Extract a meaningful error message from an error, including Axios response body parsing.
 * Mirrors the extraction logic in useAudioUpload.ts for consistency.
 */
function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    if (typeof responseData === 'string') return responseData;
    if (typeof responseData?.message === 'string') return responseData.message;
    if (typeof responseData?.detail === 'string') return responseData.detail;
    if (typeof responseData?.data?.data?.detail === 'string')
      return responseData.data.data.detail;
    if (typeof responseData?.error === 'string') return responseData.error;
  }
  if (error instanceof Error) return error.message;
  return 'Unknown error';
}

interface UseSynapseAudioUploadProps {
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  /** EstimatedVisit.id from the appointment data */
  estimatedVisitId: number | null | undefined;
  externalPatientId: string | undefined;
  pmsPatientId: string | undefined;
  imagingPatientId: string | undefined;
  /** BCP 47 language tag for the recording (e.g. 'en', 'es') */
  selectedLanguage: string;
  /** Patient name — sent as top-level field, encrypted server-side */
  patientName: string | undefined;
  currentOrganizationId: string;
  currentPracticeId: string;
  patientConsentRecordId: string | undefined;
  selectedPrompt: string | undefined;
  selectedTemplate: string | undefined;
  availableAudioDevices: AudioDeviceInfo[] | undefined;
  selectedAudioDeviceId: string | undefined;
  enableVoiceNotesConsent: boolean;
  enableVoiceNotesCustomTemplates: boolean;
  refetchClinicalNotes: () => Promise<{ data: ClinicalNote[] | undefined }>;
  refetchStandalonePatientVisitNotes: () => Promise<{
    data: ClinicalNote[] | undefined;
  }>;
  setUiStatus: (status: VideaNotesUIState) => void;
  setIsSheetOpen: (isOpen: boolean) => void;
  onSetSelectedNoteId: (noteId: string | undefined) => void;
  onGenerateSuccess: (generationTime: number) => void;
  onUploadError: (errorMeta: UploadErrorMeta) => void;
  onUploadSuccess: (s3UploadTime: number) => void;
}

interface StartAmbientSessionOptions {
  consentRecordId?: string;
  opfsSessionId?: string;
  recoveredAmbientSessionId?: string;
}

interface UseSynapseAudioUploadReturn {
  processingError: NoteGenerationErrorProps | undefined;
  startAmbientSession: (
    options?: StartAmbientSessionOptions,
  ) => Promise<string | null>;
  queueChunkForUpload: (
    chunkIndex: number,
    opfsSessionId: string,
  ) => Promise<void>;
  finishRecording: () => Promise<void>;
  /** Upload a file (blob URL) as a single chunk via Synapse — for devtools file upload flow */
  uploadFileViaSynapse: (blobUrl: string) => Promise<void>;
  ambientSessionId: string | null;
  isConfigured: boolean;
}

/**
 * Hook for uploading audio to Synapse Ambient service.
 *
 * This hook uses:
 * - Okta authentication (via oktaAuth.getOrRenewAccessToken)
 * - Chunk-based uploads to ambient.api.synapse.{env}.videahealth.io
 * - Background service worker for resilient uploads
 *
 * Flow:
 * 1. When recording starts, call startAmbientSession()
 * 2. As chunks are written to OPFS, call queueChunkForUpload(chunkIndex)
 * 3. When recording stops, call finishRecording()
 */
export const useSynapseAudioUpload = ({
  unifiedAppointmentId,
  standalonePatientVisitId,
  estimatedVisitId,
  externalPatientId,
  pmsPatientId,
  imagingPatientId,
  selectedLanguage,
  patientName,
  currentOrganizationId,
  currentPracticeId,
  patientConsentRecordId,
  selectedPrompt,
  selectedTemplate,
  availableAudioDevices,
  selectedAudioDeviceId,
  enableVoiceNotesConsent,
  enableVoiceNotesCustomTemplates,
  refetchClinicalNotes,
  refetchStandalonePatientVisitNotes,
  setUiStatus,
  setIsSheetOpen,
  onSetSelectedNoteId,
  onGenerateSuccess,
  onUploadError,
  onUploadSuccess,
}: UseSynapseAudioUploadProps): UseSynapseAudioUploadReturn => {
  const queryClient = useQueryClient();
  const { oktaAuth } = useOktaAuth();
  const [opfsSessionId, setOpfsSessionId] = useAtom(recordingSessionIdAtom);
  const liveTranscript = useAtomValue(liveTranscriptAtom);

  // Keep oktaAuth in a ref so the stable getToken callback always uses the
  // current instance without needing it in the dependency array.
  const oktaAuthRef = useRef(oktaAuth);
  oktaAuthRef.current = oktaAuth;

  // Token provider for Synapse API — calls getOrRenewAccessToken() on every
  // invocation so expired tokens are actively refreshed via the refresh token.
  // This mirrors the Axios interceptor approach (WithAxios.tsx) and works
  // correctly when the tab has been backgrounded and the cached token expired.
  const getToken = useCallback(async (): Promise<string> => {
    const token = await oktaAuthRef.current.getOrRenewAccessToken();
    if (!token) {
      throw new Error(
        'No Okta token available. Please ensure you are logged in.',
      );
    }
    return token;
  }, []);

  // Use the new atom-based Synapse upload hook
  const {
    isConfigured,
    ambientSessionId,
    error: synapseError,
    startSession,
    queueChunk,
    completeSession,
    clearSession,
  } = useSynapseUpload({
    synapseApiUrl: AMBIENT_API_URL,
    organizationId: currentOrganizationId,
    practiceId: currentPracticeId,
    getToken,
    enabled: Boolean(AMBIENT_API_URL),
  });

  const eventTracker = useAtomValue(eventTrackerAtom);
  const setNoteGenerationStep = useSetAtom(noteGenerationStepAtom);

  // Track nucleus API errors separately from Synapse errors
  const [nucleusError, setNucleusError] =
    useState<NoteGenerationErrorProps | null>(null);

  // Cache OPFS recovery metadata from startAmbientSession so finishRecording
  // can use it even if the OPFS session is deleted by AudioRecorderProvider's
  // handleRecordingComplete before finishRecording reads it.
  // Only caches fields needed by finishRecording (nucleus complete-upload).
  // patientName is intentionally excluded — it's only needed at session start (Synapse metadata).
  const cachedRecoveryMetadataRef = useRef<{
    unifiedAppointmentId?: string;
    standalonePatientVisitId?: string;
    templateId?: string;
  } | null>(null);

  // Convert Synapse error to UI error format, or use nucleus error
  const processingError: NoteGenerationErrorProps | undefined = nucleusError
    ? nucleusError
    : synapseError
      ? { title: synapseError.title, description: synapseError.description }
      : undefined;

  /**
   * Start an ambient session when recording begins.
   */
  const startAmbientSession = useCallback(
    async (options?: StartAmbientSessionOptions): Promise<string | null> => {
      const effectiveConsentId =
        options?.consentRecordId ?? patientConsentRecordId;
      const effectiveOpfsSessionId = options?.opfsSessionId ?? opfsSessionId;
      const recoveredAmbientSessionId = options?.recoveredAmbientSessionId;

      if (!isConfigured) {
        debug.warn('Synapse not configured');
        return null;
      }

      // Check consent (skip for recovery)
      if (!recoveredAmbientSessionId) {
        const hasRequiredConsent =
          !enableVoiceNotesConsent || effectiveConsentId;
        if (!hasRequiredConsent) {
          debug.warn('Missing required patient consent');
          return null;
        }
      }

      // Get appointment IDs from props, or fall back to OPFS session metadata (for recovery)
      let effectiveUnifiedAppointmentId = unifiedAppointmentId;
      let effectiveStandalonePatientVisitId = standalonePatientVisitId;
      let effectiveExternalPatientId = externalPatientId;
      let effectivePmsPatientId = pmsPatientId;
      let effectiveImagingPatientId = imagingPatientId;
      let effectivePatientName = patientName;
      let effectiveEstimatedVisitId = estimatedVisitId;
      let effectiveTemplateId = enableVoiceNotesCustomTemplates
        ? selectedTemplate
        : undefined;
      // In recovery mode, props may not have appointment context - read from OPFS
      if (
        !effectiveUnifiedAppointmentId &&
        !effectiveStandalonePatientVisitId &&
        effectiveOpfsSessionId &&
        VideaNotesOPFS.isAvailable()
      ) {
        try {
          const session = await opfs.readSession(effectiveOpfsSessionId);
          if (session?.metadata) {
            effectiveUnifiedAppointmentId =
              session.metadata.unifiedAppointmentId ?? undefined;
            effectiveStandalonePatientVisitId =
              session.metadata.standalonePatientVisitId ?? undefined;
            effectiveExternalPatientId =
              session.metadata.externalPatientId ?? effectiveExternalPatientId;
            effectivePmsPatientId =
              session.metadata.pmsPatientId ?? effectivePmsPatientId;
            effectiveImagingPatientId =
              session.metadata.imagingPatientId ?? effectiveImagingPatientId;
            effectiveTemplateId =
              session.metadata.templateId ?? effectiveTemplateId;
            effectiveEstimatedVisitId =
              session.metadata.estimatedVisitId ?? effectiveEstimatedVisitId;
            // Recover patient name from OPFS (needed for /ambient standalone notes)
            if (!effectivePatientName) {
              const recoveredName =
                formatPatientFirstNameAndLastName(
                  session.metadata.patientFirstName,
                  session.metadata.patientLastName,
                ) ?? '';
              if (recoveredName) {
                effectivePatientName = recoveredName;
              }
            }
            // Cache recovery metadata so finishRecording can use it even if
            // the OPFS session is deleted before it reads (race with handleRecordingComplete)
            cachedRecoveryMetadataRef.current = {
              unifiedAppointmentId: effectiveUnifiedAppointmentId,
              standalonePatientVisitId: effectiveStandalonePatientVisitId,
              templateId: effectiveTemplateId,
            };
            debug.log(
              'Using metadata from OPFS session for startAmbientSession:',
              {
                unifiedAppointmentId: effectiveUnifiedAppointmentId,
                standalonePatientVisitId: effectiveStandalonePatientVisitId,
                patientName: effectivePatientName,
              },
            );
          }
        } catch (error) {
          debug.warn(
            'Failed to read OPFS session metadata for startAmbientSession:',
            error,
          );
        }
      }

      return startSession({
        sessionParams: {
          externalPatientId: effectiveExternalPatientId,
          pmsPatientId: effectivePmsPatientId,
          imagingPatientId: effectiveImagingPatientId,
          patientName: effectivePatientName,
          estimatedVisitId:
            effectiveEstimatedVisitId != null
              ? String(effectiveEstimatedVisitId)
              : undefined,
          unifiedAppointmentId: effectiveUnifiedAppointmentId,
          patientConsentRecordId: effectiveConsentId,
          mimeType: 'audio/webm',
          templateId: effectiveTemplateId,
          availableAudioDevices,
          selectedAudioDeviceId,
          ...(selectedLanguage &&
            selectedLanguage !== 'en' && {
              language: selectedLanguage,
            }),
        },
        opfsSessionId: effectiveOpfsSessionId ?? undefined,
        recoveredAmbientSessionId,
      });
    },
    [
      isConfigured,
      enableVoiceNotesConsent,
      patientConsentRecordId,
      opfsSessionId,
      startSession,
      externalPatientId,
      pmsPatientId,
      imagingPatientId,
      patientName,
      estimatedVisitId,
      unifiedAppointmentId,
      standalonePatientVisitId,
      enableVoiceNotesCustomTemplates,
      selectedTemplate,
      availableAudioDevices,
      selectedAudioDeviceId,
      selectedLanguage,
    ],
  );

  /**
   * Queue a chunk for background upload.
   */
  const queueChunkForUpload = useCallback(
    async (chunkIndex: number, sessionId: string): Promise<void> => {
      await queueChunk(chunkIndex, sessionId);
    },
    [queueChunk],
  );

  /**
   * Complete recording and trigger note generation.
   */
  const finishRecording = useCallback(async (): Promise<void> => {
    if (!ambientSessionId) {
      debug.warn('No ambient session to complete');
      return;
    }

    // Clear any previous nucleus error
    setNucleusError(null);

    setNoteGenerationStep('uploading');
    let currentStep: UploadErrorMeta['step'] = 'synapse-complete';

    try {
      // Step 1: Complete Synapse session (remux + upload to nucleus bucket)
      // Pass local STT transcript (Web Speech API) to weaver for storage
      const localTranscriptText =
        liveTranscript.length > 0 ? liveTranscript.join(' ') : undefined;
      const result = await completeSession(localTranscriptText);

      if (!result) {
        throw new Error('Failed to complete Synapse session');
      }

      onUploadSuccess(result.elapsedTime);

      // Step 2: Call nucleus complete-upload directly
      currentStep = 'nucleus-complete-upload';
      const nucleusStartTime = Date.now();

      // Get values from props, or fall back to OPFS session metadata (for recovery)
      let effectiveUnifiedAppointmentId = unifiedAppointmentId;
      let effectiveStandalonePatientVisitId = standalonePatientVisitId;
      let effectiveTemplateId = enableVoiceNotesCustomTemplates
        ? selectedTemplate
        : undefined;

      // In recovery mode, props may not have appointment context - read from OPFS
      if (
        !effectiveUnifiedAppointmentId &&
        !effectiveStandalonePatientVisitId &&
        opfsSessionId &&
        VideaNotesOPFS.isAvailable()
      ) {
        try {
          const session = await opfs.readSession(opfsSessionId);
          if (session?.metadata) {
            effectiveUnifiedAppointmentId =
              session.metadata.unifiedAppointmentId ?? undefined;
            effectiveStandalonePatientVisitId =
              session.metadata.standalonePatientVisitId ?? undefined;
            // Also recover template ID if not set from props
            if (!effectiveTemplateId && session.metadata.templateId) {
              effectiveTemplateId = session.metadata.templateId;
            }
            debug.log('Using metadata from OPFS session for complete-upload:', {
              unifiedAppointmentId: effectiveUnifiedAppointmentId,
              standalonePatientVisitId: effectiveStandalonePatientVisitId,
              templateId: effectiveTemplateId,
            });
          }
        } catch (error) {
          debug.warn(
            'Failed to read OPFS session metadata for complete-upload:',
            error,
          );
        }
      }

      // Fall back to cached recovery metadata if OPFS read failed or returned empty.
      // This handles the race where AudioRecorderProvider.handleRecordingComplete
      // deletes the OPFS session before finishRecording reads it.
      if (
        !effectiveUnifiedAppointmentId &&
        !effectiveStandalonePatientVisitId &&
        cachedRecoveryMetadataRef.current
      ) {
        effectiveUnifiedAppointmentId =
          cachedRecoveryMetadataRef.current.unifiedAppointmentId;
        effectiveStandalonePatientVisitId =
          cachedRecoveryMetadataRef.current.standalonePatientVisitId;
        if (!effectiveTemplateId) {
          effectiveTemplateId = cachedRecoveryMetadataRef.current.templateId;
        }
        debug.log('Using cached recovery metadata for complete-upload:', {
          unifiedAppointmentId: effectiveUnifiedAppointmentId,
          standalonePatientVisitId: effectiveStandalonePatientVisitId,
          templateId: effectiveTemplateId,
        });
      }

      const requestBody: Record<string, unknown> = {
        sessionId: ambientSessionId,
        key: result.nucleusS3Key,
        uploadId: '',
        parts: [],
        skipMultipartCompletion: true,
        patientConsentRecordId,
        availableAudioDevices,
        selectedAudioDeviceId,
        language: selectedLanguage,
      };

      if (effectiveTemplateId) {
        requestBody.templateId = effectiveTemplateId;
      } else if (!enableVoiceNotesCustomTemplates && selectedPrompt) {
        requestBody.promptName = selectedPrompt;
      }

      if (effectiveUnifiedAppointmentId) {
        requestBody.unifiedAppointmentId = effectiveUnifiedAppointmentId;
      } else if (effectiveStandalonePatientVisitId) {
        requestBody.standalonePatientVisitId =
          effectiveStandalonePatientVisitId;
      }

      eventTracker.track('note_generation_requested', {
        template_selected: effectiveTemplateId ?? undefined,
        s3_path: result.nucleusS3Key,
      });

      setNoteGenerationStep('generating');
      // Call nucleus complete-upload
      const response = await oktaAxiosInstance.post<ClinicalNote>(
        `${configurations.API_URL}/videa-notes/complete-upload`,
        requestBody,
        {
          headers: {
            practice: currentPracticeId,
            org: currentOrganizationId,
          },
        },
      );

      const note = response.data;

      eventTracker.track('note_generation_received', {
        clinical_note_id: note.id,
        duration_seconds: result.durationSeconds,
      });

      // Only attempt refetch if we have original props (not in recovery mode).
      // In recovery mode, the refetch hooks were created with undefined IDs
      // and will make requests to /appointment-notes/undefined which fails slowly.
      const isRecoveryMode =
        !unifiedAppointmentId && !standalonePatientVisitId && opfsSessionId;

      let updatedNotesData: ClinicalNote[] | undefined;
      if (!isRecoveryMode) {
        if (effectiveUnifiedAppointmentId) {
          const refetchResult = await refetchClinicalNotes();
          updatedNotesData = refetchResult.data;
        }
        if (effectiveStandalonePatientVisitId) {
          const refetchResult = await refetchStandalonePatientVisitNotes();
          updatedNotesData = refetchResult.data;
        }
      }

      // Calculate total generation time including nucleus API call
      const totalGenerationTime =
        result.elapsedTime + (Date.now() - nucleusStartTime);
      onGenerateSuccess(totalGenerationTime);

      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: [
          endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          endpoints.ambient.voiceNotes.batchNotesByStandalonePatientVisitIds,
        ],
      });

      // Set selected note - in recovery mode, use the note from nucleus directly
      if (updatedNotesData && updatedNotesData.length > 0) {
        const updatedNote =
          updatedNotesData.find((n) => n.id === note.id) || updatedNotesData[0];
        onSetSelectedNoteId(updatedNote.id);
      } else {
        onSetSelectedNoteId(note.id);
      }

      setNoteGenerationStep('complete');
      setUiStatus(VideaNotesUIState.SUMMARY_RESULT);

      // Clean up OPFS only after successful nucleus response
      if (opfsSessionId && VideaNotesOPFS.isAvailable()) {
        try {
          await opfs.deleteSession(opfsSessionId);
          setOpfsSessionId(null);
        } catch (cleanupError) {
          debug.error('Failed to clean up OPFS:', cleanupError);
        }
      }

      // Clear Synapse and recovery state
      clearSession();
      // eslint-disable-next-line require-atomic-updates -- ref is a stable React ref, not a shared variable across concurrent iterations
      cachedRecoveryMetadataRef.current = null;
    } catch (error) {
      debug.error('Failed to complete recording:', error);

      // Extract meaningful error message from response body when available
      const errorMessage = extractErrorMessage(error);
      const statusCode = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      const responseData = axios.isAxiosError(error)
        ? error.response?.data
        : undefined;
      const isEmptyTranscript = isEmptyTranscriptError(
        statusCode,
        responseData,
      );

      // Use dedicated empty-transcript UI (422 EMPTY_TRANSCRIPT)
      if (isEmptyTranscript) {
        setNucleusError(EMPTY_TRANSCRIPT_ERROR);
      } else {
        const isNucleusError =
          errorMessage !== 'Failed to complete Synapse session';
        if (isNucleusError) {
          setNucleusError({
            title: 'Note Generation Failed',
            description: `Failed to generate clinical note: ${errorMessage}`,
          });
        }
      }

      setUiStatus(VideaNotesUIState.ERROR);
      onUploadError({
        step: currentStep,
        errorMessage: isEmptyTranscript
          ? 'Audio file produced empty transcript.'
          : errorMessage,
        statusCode,
        errorCode: isEmptyTranscript
          ? 'EMPTY_TRANSCRIPT'
          : axios.isAxiosError(error)
            ? error.response?.data?.code
            : undefined,
        url: axios.isAxiosError(error) ? error.config?.url : undefined,
      });
    } finally {
      setIsSheetOpen(true);
    }
  }, [
    ambientSessionId,
    completeSession,
    eventTracker,
    liveTranscript,
    onUploadSuccess,
    patientConsentRecordId,
    availableAudioDevices,
    selectedAudioDeviceId,
    enableVoiceNotesCustomTemplates,
    selectedTemplate,
    selectedLanguage,
    selectedPrompt,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentPracticeId,
    currentOrganizationId,
    refetchClinicalNotes,
    refetchStandalonePatientVisitNotes,
    onGenerateSuccess,
    queryClient,
    onSetSelectedNoteId,
    setUiStatus,
    opfsSessionId,
    setOpfsSessionId,
    clearSession,
    setIsSheetOpen,
    onUploadError,
    setNoteGenerationStep,
  ]);

  /**
   * Upload a file (from blob URL) via Synapse as a single chunk.
   * Used by the devtools file-upload flow (r+b+l shortcut).
   *
   * This performs the entire lifecycle in one shot:
   * 1. Create ambient session
   * 2. Fetch blob from URL
   * 3. Upload as chunk 0 via directUploadChunkToWeaver
   * 4. Call completeAndPreprocess (remux + upload to nucleus bucket)
   * 5. Call nucleus complete-upload (trigger note generation)
   * 6. Handle UI state (select note, refetch, invalidate)
   */
  const uploadFileViaSynapse = useCallback(
    async (blobUrl: string): Promise<void> => {
      if (!isConfigured) {
        debug.warn('Synapse not configured for file upload');
        return;
      }

      // Clear any previous nucleus error
      setNucleusError(null);

      let currentStep: UploadErrorMeta['step'] = 'unknown';

      try {
        // Build deps for direct ambient client calls
        const deps: AmbientClientDependencies = {
          getSynapseToken: getToken,
          synapseApiUrl: AMBIENT_API_URL,
          organizationId: currentOrganizationId,
          practiceId: currentPracticeId,
        };

        const effectiveTemplateId = enableVoiceNotesCustomTemplates
          ? selectedTemplate
          : undefined;

        // Fetch the blob first so we can detect its mime type
        debug.log('File upload: fetching blob');
        const blobResponse = await fetch(blobUrl);
        const blob = await blobResponse.blob();
        debug.log('File upload: fetched blob', blob.size, 'bytes', blob.type);

        // Step 1: Create ambient session
        currentStep = 'synapse-session-create';
        debug.log('File upload: creating ambient session');

        const sessionParams = {
          externalPatientId,
          pmsPatientId,
          imagingPatientId,
          patientName,
          estimatedVisitId:
            estimatedVisitId != null ? String(estimatedVisitId) : undefined,
          unifiedAppointmentId,
          patientConsentRecordId,
          mimeType: blob.type || 'audio/webm',
          templateId: effectiveTemplateId,
          availableAudioDevices,
          selectedAudioDeviceId,
          ...(selectedLanguage &&
            selectedLanguage !== 'en' && {
              language: selectedLanguage,
            }),
        };

        const session = await createSession(sessionParams, deps);
        const sessionId = session.id;
        debug.log('File upload: session created', sessionId);

        setNoteGenerationStep('uploading');
        // Step 3: Upload as chunk 0
        currentStep = 'synapse-chunk-upload';
        await directUploadChunkToWeaver(sessionId, 0, blob, deps);
        debug.log('File upload: chunk uploaded');

        // Step 4: Complete and preprocess (remux + upload to nucleus S3 bucket)
        currentStep = 'synapse-complete';
        const preprocessResult = await completeAndPreprocess(
          sessionId,
          { practiceId: currentPracticeId },
          deps,
        );
        debug.log(
          'File upload: preprocess complete',
          preprocessResult.nucleusS3Key,
        );

        onUploadSuccess(0);

        // Step 5: Call nucleus complete-upload
        currentStep = 'nucleus-complete-upload';
        const nucleusStartTime = Date.now();

        const requestBody: Record<string, unknown> = {
          key: preprocessResult.nucleusS3Key,
          uploadId: '',
          parts: [],
          skipMultipartCompletion: true,
          patientConsentRecordId,
          availableAudioDevices,
          selectedAudioDeviceId,
          language: selectedLanguage,
        };

        if (effectiveTemplateId) {
          requestBody.templateId = effectiveTemplateId;
        } else if (!enableVoiceNotesCustomTemplates && selectedPrompt) {
          requestBody.promptName = selectedPrompt;
        }

        if (unifiedAppointmentId) {
          requestBody.unifiedAppointmentId = unifiedAppointmentId;
        } else if (standalonePatientVisitId) {
          requestBody.standalonePatientVisitId = standalonePatientVisitId;
        }

        eventTracker.track('note_generation_requested', {
          template_selected: effectiveTemplateId ?? undefined,
          s3_path: preprocessResult.nucleusS3Key,
        });

        setNoteGenerationStep('generating');
        const nucleusResponse = await oktaAxiosInstance.post<ClinicalNote>(
          `${configurations.API_URL}/videa-notes/complete-upload`,
          requestBody,
          {
            headers: {
              practice: currentPracticeId,
              org: currentOrganizationId,
            },
          },
        );

        const note = nucleusResponse.data;

        eventTracker.track('note_generation_received', {
          clinical_note_id: note.id,
          duration_seconds: preprocessResult.durationSeconds,
        });

        // Step 6: Refetch notes and update UI
        let updatedNotesData: ClinicalNote[] | undefined;
        if (unifiedAppointmentId) {
          const refetchResult = await refetchClinicalNotes();
          updatedNotesData = refetchResult.data;
        }
        if (standalonePatientVisitId) {
          const refetchResult = await refetchStandalonePatientVisitNotes();
          updatedNotesData = refetchResult.data;
        }

        const totalGenerationTime = Date.now() - nucleusStartTime;
        onGenerateSuccess(totalGenerationTime);

        queryClient.invalidateQueries({
          queryKey: [
            endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            endpoints.ambient.voiceNotes.batchNotesByStandalonePatientVisitIds,
          ],
        });

        if (updatedNotesData && updatedNotesData.length > 0) {
          const updatedNote =
            updatedNotesData.find((n) => n.id === note.id) ||
            updatedNotesData[0];
          onSetSelectedNoteId(updatedNote.id);
        } else {
          onSetSelectedNoteId(note.id);
        }

        setNoteGenerationStep('complete');
        setUiStatus(VideaNotesUIState.SUMMARY_RESULT);

        // Clean up — no OPFS to clear for file uploads, just clear Synapse state
        clearSession();
      } catch (error) {
        debug.error('File upload via Synapse failed:', error);

        // Extract meaningful error message from response body when available
        const errorMessage = extractErrorMessage(error);
        const statusCode = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;
        const responseData = axios.isAxiosError(error)
          ? error.response?.data
          : undefined;
        const isEmptyTranscript = isEmptyTranscriptError(
          statusCode,
          responseData,
        );

        // Use dedicated empty-transcript UI (422 EMPTY_TRANSCRIPT)
        if (isEmptyTranscript) {
          setNucleusError(EMPTY_TRANSCRIPT_ERROR);
        } else {
          setNucleusError({
            title: 'Note Generation Failed',
            description: `Failed to generate clinical note from file: ${errorMessage}`,
          });
        }

        setUiStatus(VideaNotesUIState.ERROR);
        onUploadError({
          step: currentStep,
          errorMessage: isEmptyTranscript
            ? 'Audio file produced empty transcript.'
            : errorMessage,
          statusCode,
          errorCode: isEmptyTranscript
            ? 'EMPTY_TRANSCRIPT'
            : axios.isAxiosError(error)
              ? error.response?.data?.code
              : undefined,
          url: axios.isAxiosError(error) ? error.config?.url : undefined,
        });
      } finally {
        URL.revokeObjectURL(blobUrl);
        setIsSheetOpen(true);
      }
    },
    [
      isConfigured,
      eventTracker,
      getToken,
      currentOrganizationId,
      currentPracticeId,
      enableVoiceNotesCustomTemplates,
      selectedTemplate,
      externalPatientId,
      pmsPatientId,
      imagingPatientId,
      patientName,
      estimatedVisitId,
      unifiedAppointmentId,
      standalonePatientVisitId,
      patientConsentRecordId,
      availableAudioDevices,
      selectedAudioDeviceId,
      selectedLanguage,
      selectedPrompt,
      onUploadSuccess,
      refetchClinicalNotes,
      refetchStandalonePatientVisitNotes,
      onGenerateSuccess,
      queryClient,
      onSetSelectedNoteId,
      setUiStatus,
      clearSession,
      setIsSheetOpen,
      onUploadError,
      setNoteGenerationStep,
    ],
  );

  return {
    processingError,
    startAmbientSession,
    queueChunkForUpload,
    finishRecording,
    uploadFileViaSynapse,
    ambientSessionId,
    isConfigured,
  };
};
